import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { first } from 'rxjs';
import {
  EdiOrder,
  EdiOrderItem,
  EdiOrderMessageRelations,
  GungEdiError,
  GungEdiMessageItem
} from '../../../models/edi';
import { EdiDispatchAdvicesActionConfigService } from '../../../services/edi-dispatch-advices-action-config/edi-dispatch-advices-action-config.service';
import { EdiInvoicesActionConfigService } from '../../../services/edi-invoices-action-config/edi-invoices-action-config.service';
import { EdiOrderActionConfigService } from '../../../services/edi-order-action-config/edi-order-action-config.service';
import { EdiRespondeOrderResponseActionConfigService } from '../../../services/edi-order-response-action-config/edi-order-response-action-config.service';

import { EdiService } from '../../../services/edi/edi.service';
import { EdiNavigationService } from '../../../services/edi/edi-navigation.service';
import { BackendFeatureService } from '../../../services/backend-feature.service';
import { EDI_VIEWER_FEATURE_ID } from '../../../services/edi/edi-viewer-compatibility.service';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';
import { EdiModalCancelLineComponent } from '../edi-modal/edi-modal-cancel-line/edi-modal-cancel-line.component';
import { EdiModalChangeLineComponent } from '../edi-modal/edi-modal-change-line/edi-modal-change-line.component';
import { EdiRowTable, EdiRowTableType } from '../edi-row-table/edi-row-table.component';
import { EdiModalChangeQuantityComponent } from '../edi-modal/edi-modal-change-quantity/edi-modal-change-quantity.component';
import { EdiModalSkipValidationComponent } from '../edi-modal/edi-modal-skip-validation/edi-modal-skip-validation.component';

@Component({
    selector: 'lib-edi-order-detail',
    templateUrl: './edi-order-detail.component.html',
    styleUrls: ['./edi-order-detail.component.css'],
    standalone: false
})
export class EdiOrderDetailComponent implements OnInit {
   protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected ediService = inject(EdiService);
  public ediNavigationService = inject(EdiNavigationService);
  protected backendFeatureService = inject(BackendFeatureService);
  protected http = inject(HttpClient);
  protected modalService = inject(NgbModal);
  protected gungModalService = inject(GungModalService);
  public orderActionConfigService = inject(EdiOrderActionConfigService);
  public orderResponseActionConfigService = inject(EdiRespondeOrderResponseActionConfigService);
  public dispatchActionConfigService = inject(EdiDispatchAdvicesActionConfigService);
  public invoicesActionConfigService = inject(EdiInvoicesActionConfigService);
  protected translateService = inject(TranslateService);

  orderId: string;
  orderData;
  ediNavigationBaseRoute: string;
  documentViewerEnabled$ = this.backendFeatureService.isActivated(EDI_VIEWER_FEATURE_ID);

  relations: EdiOrderMessageRelations;
  invoiceMap: any;
  ordrspMap: any;
  desadvMap: any;

  invoicesToGenerate = [];
  ordrspToGenerate = [];
  despachtsToGenerate = [];

  invoiceId: string;

  itemsColumns: EdiRowTable[] = [
    {
      header: 'IMAGE',
      type: EdiRowTableType.IMAGE,
      getValue: (item: GungEdiMessageItem, index: number) => item?.extra?.images
    },
    {
      header: 'ITEM ID',
      type: EdiRowTableType.TEXT,
      getValue: (item: GungEdiMessageItem, index: number) => {
        let str = '';
        if (item.name) {
          str += this.translateService.instant('NAME', {}) + ': ' + item.name;
        }
        if (item.ids.gungId) {
          if (!!str) {
            str += '\n';
          }
          str += this.translateService.instant('ID', {}) + ': ' + item.ids.gungId;
        }
        if (item.ids.ean13) {
          if (!!str) {
            str += '\n';
          }
          str += this.translateService.instant('EAN13', {}) + ': ' + item.ids.ean13;
        }
        if (item.ids.upca) {
          if (!!str) {
            str += '\n';
          }
          str += this.translateService.instant('UPCA', {}) + ': ' + item.ids.upca;
        }
        if (item.ids.buyerId) {
          if (!!str) {
            str += '\n';
          }
          str += this.translateService.instant('BUYER', {}) + ': ' + item.ids.buyerId;
        }
        return str;
      },
      customClass: 'formatted'
    },
    {
      header: 'QUANTITY',
      type: EdiRowTableType.TEXT,
      getValue: (item: GungEdiMessageItem, index: number) => {
        if (item.quantities.expectedQuantity) {
          return 'EXP: ' + item.quantities.expectedQuantity;
        }
        return '';
      }
    },
    {
      header: 'PRICE',
      type: EdiRowTableType.TEXT,
      getValue: (item: GungEdiMessageItem, index: number) => {
        let val = '';
        if (item.prices.expectedUnitNetPrice) {
          val += 'EXP: ' + item.prices.expectedUnitNetPrice.value;
        }
        if (item.prices.actualUnitNetPrice) {
          val += ' ACTUAL: ' + item.prices.actualUnitNetPrice.value;
        }
        return val;
      }
    },
    {
      header: 'DELIVERY_DATE',
      type: EdiRowTableType.HTML,
      getValue: (item: GungEdiMessageItem, index: number) => {
        let result = `${item.deliveryDate ?? ''}`;
        if (item.changes.cancelled) {
          result += `
                      <span class="d-block">
                        <i class="fa-solid fa-triangle-exclamation text-danger mr-1"></i>
                        <span>${this.translateService.instant('CANCELED', {})}</span>
                      </span
          `;
        }
        return result;
      }
    },
    {
      header: 'SECONDARY_TABLE',
      type: EdiRowTableType.TABLE,
      customClass: 'error-table',
      getValue: (item: GungEdiMessageItem, index: number) => item.errors,
      secondaryColumn: () => [
        {
          header: 'Type',
          type: EdiRowTableType.TEXT,
          getValue: (error: GungEdiError) => error.type
        },
        {
          header: 'Message',
          type: EdiRowTableType.TEXT,
          getValue: (error: GungEdiError) => error.error
        },
        {
          header: 'Source',
          type: EdiRowTableType.TEXT,
          getValue: (error: GungEdiError) => error.validator
        },
        {
          header: 'Action',
          type: EdiRowTableType.ACTION,
          getValue: (error: GungEdiError) => {
            const actions = [
              {
                header: 'CANCEL_LINE',
                getValue: (ctx: { item: GungEdiMessageItem; index: number }) => this.cancelLineAndRevalidate(ctx.item, ctx.index),
                customClass: 'btn btn-sm btn-block btn-primary'
              },
              {
                header: 'CHANGE_ITEM_ID',
                getValue: (ctx: { item: GungEdiMessageItem; index: number }) => this.changeLineAndRevalidate(ctx.item, ctx.index),
                customClass: 'btn btn-sm btn-block btn-secondary'
              }
            ];

            if (
              error.validator === 'EdiQuantityValidator' ||
              error.validator === 'EdiEmailScannerManualReviewValidator'
            ) {
              actions.push({
                header: 'CHANGE_QUANTITY',
                getValue: (ctx: { item: GungEdiMessageItem; index: number }) => this.changeQuantityAndRevalidate(ctx.item, ctx.index),
                customClass: 'btn btn-sm btn-block btn-secondary'
              });
            }

            if (error.validator === 'EdiEmailScannerManualReviewValidator') {
              actions.push({
                header: 'SKIP_VALIDATION',
                getValue: (ctx: { item: GungEdiMessageItem; index: number }) => this.markAsSkipValidation(ctx.item, ctx.index),
                customClass: 'btn btn-sm btn-block btn-secondary'
              });
            }

            return actions;
          }
        }
      ]
    }
  ];

  itemRowClass = (item: GungEdiMessageItem) =>
    item.changes?.cancelled ? 'bg-cancelled' : '';

  constructor() {
    this.invoiceMap = {};
    this.ordrspMap = {};
    this.desadvMap = {};
  }

  ngOnInit(): void {
    this.route.params.subscribe(data => {
      this.orderId = data.id;
      this.ediService.getOrder(this.orderId).subscribe(orderData => {
        this.orderData = orderData.data;
      });

      const urlSegments = this.router.url.split('?')[0].split('/');
      this.ediNavigationBaseRoute = urlSegments.slice(0, -1).join('/');
      this.ediNavigationService.setNavigationCurrentId(this.ediNavigationBaseRoute, this.orderId);

      this.updateRelations();
    });
  }

  updateRelations(): void {
    this.invoiceMap = {};
    this.ordrspMap = {};
    this.desadvMap = {};
    this.invoicesToGenerate = [];
    this.ordrspToGenerate = [];
    this.despachtsToGenerate = [];

    this.ediService
      .getOrderMessageRelations(this.orderId)
      .pipe(first())
      .subscribe(relations => {
        this.relations = relations;
        if (this.relations.erpInvoiceIds) {
          this.relations.erpInvoiceIds.forEach(invoiceId => {
            this.getInvoicesMessagesByOrder(invoiceId)
              .pipe(first())
              .subscribe(ediInvoices => {
                if (ediInvoices.length > 0) {
                  this.invoiceMap[invoiceId] = ediInvoices;
                } else {
                  this.invoicesToGenerate.push(invoiceId);
                }
              });
          });
        }

        if (this.relations.erpDesadvIds) {
          this.relations.erpDesadvIds.forEach(despatchId => {
            this.getDespatchsMessagesByOrder(despatchId)
              .pipe(first())
              .subscribe(ediDespatches => {
                if (ediDespatches.length > 0) {
                  this.desadvMap[despatchId] = ediDespatches;
                } else {
                  this.despachtsToGenerate.push(despatchId);
                }
              });
          });
        }

        if (this.relations.erpOrderIds) {
          this.relations.erpOrdrspIds.forEach(orderId => {
            this.getOrdersMessagesByOrder(orderId)
              .pipe(first())
              .subscribe(ediOrdrsps => {
                if (ediOrdrsps.length > 0) {
                  this.ordrspMap[orderId] = ediOrdrsps;
                } else {
                  this.ordrspToGenerate.push(orderId);
                }
              });
          });
        }
      });
  }

  getInvoicesMessagesByOrder(invoiceId): Observable<any> {
    return this.http.get('json/edi/v2/invoice/messages/by-invoice-id?invoiceId=' + invoiceId);
  }

  getDespatchsMessagesByOrder(despatchId): Observable<any> {
    return this.http.get('json/edi/v2/despatch/messages/by-despatch-id?despatchId=' + despatchId);
  }

  getOrdersMessagesByOrder(orderId): Observable<any> {
    return this.http.get('json/edi/v2/order-response/messages/by-order-id?orderId=' + orderId);
  }

  sendToErp() {
    const data = { data: this.orderData };
    this.ediService.sendToErp(this.orderId, data).subscribe(postdata => {
      console.log(postdata);
    });
  }

  cancelLineAndRevalidate(line: any, index: number) {
    const modalRef = this.modalService.open(EdiModalCancelLineComponent, {
      ariaLabelledBy: 'modal-basic-title',
      size: 'lg',
      backdrop: 'static'
    });
    modalRef.componentInstance.ediOrderId = this.orderData.id;
    modalRef.componentInstance.ediOrderItem = line;
    modalRef.componentInstance.index = index;
    modalRef.result.then(
      result => {
        if (result) {
          this.refreshItemsTable();
          if (result.validate) {
            this.validateAgain();
          }
          this.gungModalService
            .openConfirmYesNoModal(undefined, 'Sucessfully cancelled line', { size: 'sm' }, 'OK', null)
            .then(_ => {});
        }
      },
      reason => {}
    );
  }

  changeLineAndRevalidate(line: EdiOrderItem, index: number) {
    const modalRef = this.modalService.open(EdiModalChangeLineComponent, {
      ariaLabelledBy: 'modal-basic-title',
      size: 'lg',
      backdrop: 'static'
    });
    modalRef.componentInstance.ediOrderId = this.orderData.id;
    modalRef.componentInstance.ediOrderItem = line;
    modalRef.componentInstance.index = index;
    modalRef.result.then(
      result => {
        if (result && result.response) {
          this.refreshItemsTable();
          if (result.validate) {
            this.validateAgain();
          }
          this.gungModalService
            .openConfirmYesNoModal(undefined, 'Sucessfully changed line', { size: 'sm' }, 'OK', null)
            .then(_ => {});
        }
      },
      reason => {}
    );
  }

  changeQuantityAndRevalidate(line: EdiOrderItem, index: number): void {
    const modalRef = this.modalService.open(EdiModalChangeQuantityComponent, {
      ariaLabelledBy: 'modal-basic-title',
      size: 'lg',
      backdrop: 'static'
    });
    modalRef.componentInstance.ediOrderId = this.orderData.id;
    modalRef.componentInstance.ediOrderItem = line;
    modalRef.componentInstance.index = index;
    modalRef.result.then(
      result => {
        if (result && result.response) {
          this.refreshItemsTable();
          if (result.validate) {
            this.validateAgain();
          }
          this.gungModalService
            .openConfirmYesNoModal(undefined, 'Sucessfully changed line', { size: 'sm' }, 'OK', null)
            .then(_ => {});
        }
      },
      reason => {}
    );
  }

  markAsSkipValidation(line: EdiOrderItem, index: number): void {
    const modalRef = this.modalService.open(EdiModalSkipValidationComponent, {
      ariaLabelledBy: 'modal-basic-title',
      size: 'lg',
      backdrop: 'static'
    });
    modalRef.componentInstance.ediOrderId = this.orderData.id;
    modalRef.componentInstance.ediOrderItem = line;
    modalRef.componentInstance.index = index;
    modalRef.result.then(
      result => {
        if (result && result.response) {
          this.refreshItemsTable();
          if (result.validate) {
            this.validateAgain();
          }
          this.gungModalService
            .openConfirmYesNoModal(undefined, 'Sucessfully changed line', { size: 'sm' }, 'OK', null)
            .then(_ => {});
        }
      },
      reason => {}
    );
  }

  refreshItemsTable(): void {
    // The modal mutates the item in-place; create a new items array reference so
    // Angular detects a change and triggers ngOnChanges on lib-edi-row-table.
    this.orderData = { ...this.orderData, items: [...this.orderData.items] };
  }

  validateAgain(): any {
    this.http
      .get('json/edi/v2/order/messages/revalidate-by-message-id?messageId=' + this.orderId)
      .pipe(first())
      .subscribe(newOrderData => {
        this.orderData = newOrderData;
        this.updateRelations();
      });
  }

  sendOrderResponseByOrderId(orderId: string): any {
    this.http
      .get('json/edi/v2/actuator/ordrsp/generate-by-order-id-get?orderId=' + orderId)
      .pipe(first())
      .subscribe(response => {});
  }
  sendInvoiceByInvoiceId(invoiceId: string): any {
    this.http
      .get('json/edi/v2/actuator/invoice/generate-by-invoice-id-get?invoiceId=' + invoiceId+'&ediConfig='+this.orderData.ediConfigId)
      .pipe(first())
      .subscribe(response => {});
  }
  sendDesadvByDesadvId(despatchId: string): any {
    this.http
      .get('json/edi/v2/actuator/desadv/generate-by-despatch-system-id-get?despatchSystemId=' + despatchId)
      .pipe(first())
      .subscribe(response => {});
  }

  sendOrderResponse() {
    const data = { data: this.orderData };
    this.ediService.sendOrderResponse(this.orderId, data).subscribe(postdata => {
      console.log(postdata);
    });
  }

  sendInvoices() {}
}
